import React, { useState, useEffect, useCallback } from "react";
import {
  Row,
  Col,
  Card,
  Space,
  Button,
  Statistic,
  Select,
  Modal,
  Input,
  message,
} from "antd";
import {
  PlusOutlined,
  SyncOutlined,
  CalendarOutlined,
} from "@ant-design/icons";
import useCurricularCalendars from "../../../hooks/academics/useCurricularCalendars";
import useCalendarEvents from "../../../hooks/academics/useCalendarEvents";
import useAcademicYears from "../../../hooks/common/useAcademicYears";
import useTerms from "../../../hooks/common/useTerms";
import CurricularCalendarTable from "../../../components/academics/curricular_calendars/CurricularCalendarTable";
import CurricularCalendarFormModal from "../../../components/academics/curricular_calendars/CurricularCalendarFormModal";
import CurricularCalendarView from "../../../components/academics/curricular_calendars/CurricularCalendarView";
import CalendarEventFormModal from "../../../components/academics/calendar_events/CalendarEventFormModal";
import ValuesStore from "../../../store/values-store";

const STATUS_OPTIONS = [
  { value: "", label: "All Statuses" },
  { value: "Draft", label: "Draft" },
  { value: "Pending Head Teacher Approval", label: "Pending HT Approval" },
  { value: "Pending Deputy Principal Approval", label: "Pending DP Approval" },
  { value: "Pending Principal Approval", label: "Pending Principal Approval" },
  { value: "Published", label: "Published" },
];

const CurricularCalendarsManagement = () => {
  const valuesStore = ValuesStore();
  const myRoles =
    valuesStore.getValue("myRoles") ||
    JSON.parse(sessionStorage.getItem("myRoles") || "[]");
  const isAdmin =
    Array.isArray(myRoles) && myRoles.includes("System Administrator");

  const {
    calendars,
    loading: calLoading,
    fetchCalendars,
    createCalendar,
    deleteCalendar,
    submitCalendar,
    approveCalendar,
    rejectCalendar,
  } = useCurricularCalendars();

  const {
    loading: evtLoading,
    fetchEvents,
    createEvent,
    updateEvent,
    deleteEvent,
    cancelEvent,
  } = useCalendarEvents();

  const { academicYears, fetchAcademicYears } = useAcademicYears();
  const { terms, fetchTerms } = useTerms();

  // Calendar list filters
  const [filterAcadyrID, setFilterAcadyrID] = useState(null);
  const [filterTermID, setFilterTermID] = useState(null);
  const [filterStatus, setFilterStatus] = useState("");

  // Create calendar modal
  const [calModalVisible, setCalModalVisible] = useState(false);

  // Calendar view (Google Calendar UI)
  const [calViewVisible, setCalViewVisible] = useState(false);
  const [selectedCalendar, setSelectedCalendar] = useState(null);
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);

  // Event form modal
  const [eventFormVisible, setEventFormVisible] = useState(false);
  const [editEventRecord, setEditEventRecord] = useState(null);
  const [defaultEventValues, setDefaultEventValues] = useState(null);

  // Cancel event modal
  const [cancelEvtVisible, setCancelEvtVisible] = useState(false);
  const [cancelEvtTarget, setCancelEvtTarget] = useState(null);
  const [cancelEvtReason, setCancelEvtReason] = useState("");

  // Reject calendar modal
  const [rejectCalVisible, setRejectCalVisible] = useState(false);
  const [rejectCalTarget, setRejectCalTarget] = useState(null);
  const [rejectCalReason, setRejectCalReason] = useState("");

  const load = useCallback(() => {
    const params = {};
    if (filterAcadyrID) params.acadyrID = filterAcadyrID;
    if (filterTermID) params.termID = filterTermID;
    if (filterStatus) params.status = filterStatus;
    fetchCalendars(params);
  }, [fetchCalendars, filterAcadyrID, filterTermID, filterStatus]);

  const loadCalendarEvents = useCallback(
    async (calendarID) => {
      setEventsLoading(true);
      const data = await fetchEvents({ calendarID });
      setCalendarEvents(Array.isArray(data) ? data : []);
      setEventsLoading(false);
    },
    [fetchEvents],
  );

  useEffect(() => {
    fetchAcademicYears();
    fetchTerms();
  }, [fetchAcademicYears, fetchTerms]);

  useEffect(() => {
    load();
  }, [load]);

  // ── Calendar CRUD ──────────────────────────────────────────────

  async function handleCreateCalendar(values) {
    const result = await createCalendar(values);
    if (result) {
      setCalModalVisible(false);
      load();
    }
  }

  async function handleViewCalendar(calendar) {
    setSelectedCalendar(calendar);
    setCalViewVisible(true);
    await loadCalendarEvents(calendar.id);
  }

  async function handleSubmitCalendar(id) {
    const result = await submitCalendar(id);
    if (result) {
      load();
      if (selectedCalendar?.id === id) setSelectedCalendar(result);
    }
  }

  async function handleApproveCalendar(id) {
    const result = await approveCalendar(id);
    if (result) {
      load();
      if (selectedCalendar?.id === id) setSelectedCalendar(result);
    }
  }

  function openRejectCalendar(calendar) {
    setRejectCalTarget(calendar);
    setRejectCalReason("");
    setRejectCalVisible(true);
  }

  async function handleRejectCalendar() {
    if (!rejectCalReason.trim()) {
      message.warning("Please provide a rejection reason");
      return;
    }
    const result = await rejectCalendar(
      rejectCalTarget.id,
      rejectCalReason.trim(),
    );
    if (result) {
      setRejectCalVisible(false);
      load();
      if (selectedCalendar?.id === rejectCalTarget.id)
        setSelectedCalendar(result);
    }
  }

  async function handleDeleteCalendar(id) {
    const ok = await deleteCalendar(id);
    if (ok) load();
  }

  // ── Event CRUD (within calendar view) ─────────────────────────

  function handleAddEvent(date) {
    setEditEventRecord(null);
    setDefaultEventValues({
      calendarID: selectedCalendar?.id,
      scheduledDate: date,
    });
    setEventFormVisible(true);
  }

  function handleEditEvent(event) {
    setEditEventRecord(event);
    setDefaultEventValues(null);
    setEventFormVisible(true);
  }

  async function handleSaveEvent(values) {
    const result = editEventRecord
      ? await updateEvent(editEventRecord.id, values)
      : await createEvent(values);
    if (result) {
      setEventFormVisible(false);
      setEditEventRecord(null);
      if (selectedCalendar) await loadCalendarEvents(selectedCalendar.id);
    }
  }

  function handleOpenCancelEvent(event) {
    setCancelEvtTarget(event);
    setCancelEvtReason("");
    setCancelEvtVisible(true);
  }

  async function handleCancelEvent() {
    if (!cancelEvtReason.trim()) {
      message.warning("Please provide a cancellation reason");
      return;
    }
    const result = await cancelEvent(
      cancelEvtTarget.id,
      cancelEvtReason.trim(),
    );
    if (result) {
      setCancelEvtVisible(false);
      if (selectedCalendar) await loadCalendarEvents(selectedCalendar.id);
    }
  }

  async function handleDeleteEvent(id) {
    const ok = await deleteEvent(id);
    if (ok && selectedCalendar) await loadCalendarEvents(selectedCalendar.id);
  }

  // ── Stats ──────────────────────────────────────────────────────
  const published = calendars.filter((c) => c.status === "Published").length;
  const pending = calendars.filter(
    (c) => c.status !== "Draft" && c.status !== "Published",
  ).length;
  const drafts = calendars.filter((c) => c.status === "Draft").length;

  return (
    <div style={{ padding: 16 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 16,
        }}
      >
        <h4 style={{ margin: 0 }}>
          <CalendarOutlined style={{ marginRight: 8 }} />
          Co-Curricular Calendars
        </h4>
        <Space>
          <Button icon={<SyncOutlined />} onClick={load}>
            Refresh
          </Button>
          {isAdmin && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setCalModalVisible(true)}
            >
              New Calendar
            </Button>
          )}
        </Space>
      </div>

      {/* Stats */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic title="Total" value={calendars.length} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic
              title="Published"
              value={published}
              valueStyle={{ color: "#52c41a" }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic
              title="Pending Approval"
              value={pending}
              valueStyle={{ color: "#faad14" }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic title="Draft" value={drafts} />
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Space wrap>
          <Select
            placeholder="Academic Year"
            allowClear
            style={{ width: 160 }}
            onChange={setFilterAcadyrID}
            options={academicYears.map((y) => ({
              value: y.id,
              label: y.acadyr,
            }))}
          />
          <Select
            placeholder="Term"
            allowClear
            style={{ width: 140 }}
            onChange={setFilterTermID}
            options={terms.map((t) => ({ value: t.id, label: t.name }))}
          />
          <Select
            placeholder="Status"
            allowClear
            style={{ width: 220 }}
            onChange={(v) => setFilterStatus(v || "")}
            options={STATUS_OPTIONS}
          />
        </Space>
      </Card>

      <CurricularCalendarTable
        calendars={calendars}
        loading={calLoading}
        isAdmin={isAdmin}
        onView={handleViewCalendar}
        onSubmit={handleSubmitCalendar}
        onApprove={handleApproveCalendar}
        onReject={openRejectCalendar}
        onDelete={handleDeleteCalendar}
      />

      {/* Create calendar modal */}
      <CurricularCalendarFormModal
        visible={calModalVisible}
        onCancel={() => setCalModalVisible(false)}
        onSave={handleCreateCalendar}
        loading={calLoading}
        academicYears={academicYears}
        terms={terms}
      />

      {/* Google Calendar-style view */}
      {(() => {
        const calTerm = terms.find((t) => t.id === selectedCalendar?.termID);
        return (
          <CurricularCalendarView
            visible={calViewVisible}
            onClose={() => setCalViewVisible(false)}
            calendar={selectedCalendar}
            events={calendarEvents}
            loading={eventsLoading}
            isAdmin={isAdmin}
            termStart={calTerm?.start}
            termEnd={calTerm?.end}
            onAddEvent={handleAddEvent}
            onEditEvent={handleEditEvent}
            onCancelEvent={handleOpenCancelEvent}
            onDeleteEvent={handleDeleteEvent}
            onSubmit={handleSubmitCalendar}
            onApprove={handleApproveCalendar}
            onReject={openRejectCalendar}
            onRefresh={() =>
              selectedCalendar && loadCalendarEvents(selectedCalendar.id)
            }
          />
        );
      })()}

      {/* Create/edit event modal */}
      <CalendarEventFormModal
        visible={eventFormVisible}
        onCancel={() => {
          setEventFormVisible(false);
          setEditEventRecord(null);
        }}
        onSave={handleSaveEvent}
        loading={evtLoading}
        editRecord={editEventRecord}
        calendars={calendars}
        terms={terms}
        defaultValues={defaultEventValues}
      />

      {/* Cancel event modal */}
      <Modal
        title="Cancel Event"
        open={cancelEvtVisible}
        onCancel={() => setCancelEvtVisible(false)}
        onOk={handleCancelEvent}
        okButtonProps={{ danger: true }}
        okText="Cancel Event"
        confirmLoading={evtLoading}
      >
        <p>
          Please provide a reason for cancelling{" "}
          <strong>{cancelEvtTarget?.title}</strong>:
        </p>
        <Input.TextArea
          rows={3}
          value={cancelEvtReason}
          onChange={(e) => setCancelEvtReason(e.target.value)}
          placeholder="Cancellation reason..."
        />
      </Modal>

      {/* Reject calendar modal */}
      <Modal
        title="Reject Calendar"
        open={rejectCalVisible}
        onCancel={() => setRejectCalVisible(false)}
        onOk={handleRejectCalendar}
        okButtonProps={{ danger: true }}
        okText="Reject"
        confirmLoading={calLoading}
      >
        <p>Please provide a reason for rejecting this calendar:</p>
        <Input.TextArea
          rows={3}
          value={rejectCalReason}
          onChange={(e) => setRejectCalReason(e.target.value)}
          placeholder="Rejection reason..."
        />
      </Modal>
    </div>
  );
};

export default CurricularCalendarsManagement;
